/*
 * <cart-recommendations> — renders complementary products inside the cart drawer.
 * Fetches, for each cart product, the complementary products curated in the
 * Search & Discovery app (/recommendations/products.json?intent=complementary).
 * Shows them as a slider: one card at a time, arrows on the header and dots below.
 *
 * As the cart drawer re-renders its whole #CartDrawer markup on every cart
 * update, this custom element is recreated and connectedCallback re-fetches,
 * keeping the recommendations in sync automatically.
 */
class CartRecommendations extends HTMLElement {
  static LIST_ID = 'cart_upsell';
  static LIST_NAME = 'Carrinho - Aproveite e leve também';
  // Lists already reported with view_item_list on this page (by product ids)
  static reportedLists = new Set();

  connectedCallback() {
    this.list = this.querySelector('.cart-recommendations__list');
    const seedIds = this.parseIds(this.dataset.productIds);
    // Optional cap on the number of cards; without data-limit every recommendation is shown
    const limit = parseInt(this.dataset.limit, 10) || Infinity;

    if (!seedIds.length || !this.list) {
      this.hidden = true;
      return;
    }

    // ids already in the cart — excluded from the merged recommendations
    this.excludeIds = new Set(this.parseIds(this.dataset.cartProductIds));

    this.loadRecommendations(seedIds, limit);
  }

  parseIds(value) {
    return (value || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  async loadRecommendations(seedIds, limit) {
    try {
      const products = this.mergeProducts(await this.fetchComplementary(seedIds), limit);

      if (!products.length) {
        this.hidden = true;
        return;
      }

      this.render(products);
    } catch (error) {
      console.error('[cart-recommendations]', error);
      this.hidden = true;
    }
  }

  // One request per cart seed product (the API only accepts a single product_id).
  // Resolves to one list of products per seed; a failed request counts as an empty list.
  fetchComplementary(seedIds) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    return Promise.all(
      seedIds.map((productId) => {
        const url = `${root}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`;
        return fetch(url)
          .then((response) => response.json())
          .then((data) => data.products || [])
          .catch(() => []);
      })
    );
  }

  // Interleaves each seed's results (round-robin) so relevance stays balanced,
  // then dedupes by id, drops unavailable / already-in-cart products and caps the list.
  mergeProducts(lists, limit) {
    const merged = [];
    const seen = new Set();
    const maxLength = Math.max(0, ...lists.map((list) => list.length));

    for (let i = 0; i < maxLength; i += 1) {
      for (const list of lists) {
        const product = list[i];
        if (!product || !product.available) continue;

        const id = String(product.id);
        if (seen.has(id) || this.excludeIds.has(id)) continue;

        seen.add(id);
        merged.push(product);

        if (merged.length >= limit) return merged;
      }
    }

    return merged;
  }

  render(products) {
    // Only products with an in-stock variant become cards; the card index matches this.products
    this.products = products.filter((product) => this.availableVariant(product));
    if (!this.products.length) {
      this.hidden = true;
      return;
    }

    this.list.innerHTML = this.products.map((product, index) => this.cardHTML(product, index)).join('');
    this.hidden = false;
    this.bindAddButtons();
    this.bindSelectItem();
    this.setupSlider();
    this.enableDragScroll();
    this.observeViewItemList();
  }

  availableVariant(product) {
    return (product.variants || []).find((item) => item.available);
  }

  // Arrows and dots: each step moves one card; the dots follow the scroll position.
  setupSlider() {
    const cards = this.cards();
    const nav = this.querySelector('.cart-recommendations__nav');
    this.dots = this.querySelector('.cart-recommendations__dots');
    this.prevButton = this.querySelector('.cart-recommendations__arrow--prev');
    this.nextButton = this.querySelector('.cart-recommendations__arrow--next');

    const single = cards.length < 2;
    if (nav) nav.hidden = single;
    if (this.dots) {
      this.dots.hidden = single;
      this.dots.innerHTML = cards
        .map(
          (_, index) =>
            `<button type="button" class="cart-recommendations__dot" aria-label="Produto ${index + 1} de ${cards.length}"></button>`
        )
        .join('');
      this.dots.querySelectorAll('button').forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });
    }
    if (single) return;

    this.prevButton.addEventListener('click', () => this.goTo(this.currentIndex() - 1));
    this.nextButton.addEventListener('click', () => this.goTo(this.currentIndex() + 1));

    let frame = null;
    this.list.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        this.updateSlider();
      });
    });
    this.updateSlider();
  }

  cards() {
    return Array.from(this.list.querySelectorAll('.cart-recommendations__card'));
  }

  // Scroll position of each card (the first one starts at 0)
  cardOffset(card) {
    return card.offsetLeft - this.cards()[0].offsetLeft;
  }

  currentIndex() {
    const cards = this.cards();
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    // At the end the last card can't reach the left edge, so it counts as current
    if (this.list.scrollLeft >= maxScroll - 2) return cards.length - 1;

    let nearest = 0;
    cards.forEach((card, index) => {
      const distance = Math.abs(this.cardOffset(card) - this.list.scrollLeft);
      if (distance < Math.abs(this.cardOffset(cards[nearest]) - this.list.scrollLeft)) nearest = index;
    });
    return nearest;
  }

  goTo(index) {
    const cards = this.cards();
    const target = cards[Math.max(0, Math.min(index, cards.length - 1))];
    if (target) this.list.scrollTo({ left: this.cardOffset(target), behavior: 'smooth' });
  }

  updateSlider() {
    const index = this.currentIndex();
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;

    if (this.prevButton) this.prevButton.disabled = this.list.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.list.scrollLeft >= maxScroll - 2;
    if (this.dots) {
      this.dots.querySelectorAll('button').forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.toggleAttribute('aria-current', dotIndex === index);
      });
    }
  }

  // Mouse click-and-drag (grab) scrolling; on release it settles on the nearest card.
  // Touch keeps the native swipe.
  enableDragScroll() {
    const el = this.list;
    if (el.dataset.dragBound) return;
    el.dataset.dragBound = 'true';

    let isDown = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;

    el.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      isDown = true;
      moved = false;
      startX = event.pageX;
      startScroll = el.scrollLeft;
      el.classList.add('is-grabbing');
      el.setPointerCapture(event.pointerId);
    });

    el.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const walk = event.pageX - startX;
      if (Math.abs(walk) > 3) moved = true;
      el.scrollLeft = startScroll - walk;
    });

    const stop = (event) => {
      if (!isDown) return;
      isDown = false;
      if (event.pointerId != null && el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      const index = this.currentIndex();
      el.classList.remove('is-grabbing');
      this.goTo(index);
    };

    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);

    // avoid triggering card interactions when the pointer was dragged
    el.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  cardHTML(product, index) {
    // Only an in-stock variant can be added; without one the card is not shown
    const variant = this.availableVariant(product);
    if (!variant) return '';

    const image = product.featured_image || '';
    const isBestSeller = (product.tags || []).some((tag) =>
      ['mais vendido', 'mais-vendido', 'bestseller', 'best-seller'].includes(String(tag).toLowerCase())
    );

    return `
      <div class="cart-recommendations__card" data-index="${index}">
        <a href="${product.url}" class="cart-recommendations__image" tabindex="-1" aria-hidden="true">
          ${isBestSeller ? '<span class="cart-recommendations__badge">Mais vendido</span>' : ''}
          ${image ? `<img src="${image}" alt="${this.escape(product.title)}" loading="lazy">` : ''}
        </a>
        <div class="cart-recommendations__info">
          <a href="${product.url}" class="cart-recommendations__title">${this.escape(product.title)}</a>
          <span class="cart-recommendations__price">${this.formatPrice(product.price)}</span>
        </div>
        <button
          type="button"
          class="cart-recommendations__add button"
          data-variant-id="${variant.id}"
          ${variant.available ? '' : 'disabled'}
        >
          <span class="cart-recommendations__add-icon" aria-hidden="true">+</span>
          Adicionar
          <span class="loading__spinner hidden">
            <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
          </span>
        </button>
      </div>
    `;
  }

  bindAddButtons() {
    this.list.querySelectorAll('.cart-recommendations__add').forEach((button) => {
      button.addEventListener('click', () => this.addToCart(button));
    });
  }

  setLoading(button, isLoading) {
    const spinner = button.querySelector('.loading__spinner');
    button.classList.toggle('loading', isLoading);
    button.toggleAttribute('aria-disabled', isLoading);
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.classList.contains('loading')) return;

    const cart = document.querySelector('cart-drawer');

    this.setLoading(button, true);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
    }

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const addUrl = (window.routes && routes.cart_add_url) || '/cart/add.js';

    fetch(addUrl, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.setLoading(button, false);
          return;
        }

        this.trackAddToCart(response, Number(button.closest('.cart-recommendations__card').dataset.index));

        if (cart) {
          cart.renderContents(response);
        } else {
          window.location = (window.routes && routes.cart_url) || '/cart';
        }
      })
      .catch((error) => {
        console.error('[cart-recommendations]', error);
        this.setLoading(button, false);
      });
  }

  // ---- GA4 (dataLayer) ----------------------------------------------------
  // view_item_list, select_item and add_to_cart, all flagged with the upsell list so
  // GA4 can tell it apart from the other lists and add-to-cart flows.

  pushEcommerce(eventName, ecommerce) {
    try {
      if (window.pushEcommerce) {
        window.pushEcommerce(eventName, ecommerce);
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
    } catch (error) {
      console.error(`[GA4] ${eventName} push falhou:`, error);
    }
  }

  currency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || undefined;
  }

  // GA4 item from a recommendations API product (price in cents)
  listItem(product, index) {
    const variant = this.availableVariant(product);
    return {
      item_id: String(product.id),
      item_name: product.title,
      item_variant: variant && variant.title !== 'Default Title' ? variant.title : undefined,
      item_brand: product.vendor || undefined,
      item_category: product.type || undefined,
      item_list_id: CartRecommendations.LIST_ID,
      item_list_name: CartRecommendations.LIST_NAME,
      index,
      price: (product.price || 0) / 100,
      quantity: 1,
    };
  }

  // view_item_list once the upsell is actually on screen (the drawer may be rendered closed).
  // The drawer re-renders on every cart change, so the same list is only reported once per page.
  observeViewItemList() {
    if (this.viewObserver) this.viewObserver.disconnect();

    const report = () => {
      const signature = this.products.map((product) => product.id).join(',');
      if (CartRecommendations.reportedLists.has(signature)) return;
      CartRecommendations.reportedLists.add(signature);

      this.pushEcommerce('view_item_list', {
        item_list_id: CartRecommendations.LIST_ID,
        item_list_name: CartRecommendations.LIST_NAME,
        items: this.products.map((product, index) => this.listItem(product, index)),
      });
    };

    if (!('IntersectionObserver' in window)) {
      report();
      return;
    }

    this.viewObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.viewObserver.disconnect();
        report();
      },
      { threshold: 0.5 }
    );
    this.viewObserver.observe(this.list);
  }

  // select_item when the product image or title of a card is clicked
  bindSelectItem() {
    this.list.querySelectorAll('.cart-recommendations__image, .cart-recommendations__title').forEach((link) => {
      link.addEventListener('click', () => {
        const index = Number(link.closest('.cart-recommendations__card').dataset.index);
        const product = this.products[index];
        if (!product) return;

        this.pushEcommerce('select_item', {
          item_list_id: CartRecommendations.LIST_ID,
          item_list_name: CartRecommendations.LIST_NAME,
          items: [this.listItem(product, index)],
        });
      });
    });
  }

  // add_to_cart (same payload as the other add buttons) from the /cart/add.js response
  trackAddToCart(response, index) {
    const addedItems = response.items || [response];

    this.pushEcommerce('add_to_cart', {
      currency: this.currency(),
      value: addedItems.reduce((sum, item) => sum + ((item.price || 0) / 100) * (item.quantity || 1), 0),
      items: addedItems.map((item) => ({
        item_id: String(item.product_id != null ? item.product_id : item.id),
        item_name: item.product_title || item.title,
        item_variant: item.variant_title || undefined,
        item_brand: item.vendor || undefined,
        item_category: item.product_type || undefined,
        item_list_id: CartRecommendations.LIST_ID,
        item_list_name: CartRecommendations.LIST_NAME,
        index: Number.isNaN(index) ? undefined : index,
        price: (item.price || 0) / 100,
        quantity: item.quantity || 1,
      })),
    });
  }

  formatPrice(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'BRL';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format((cents || 0) / 100);
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : value;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
